import React, { useState, useEffect } from "react";
import {
  fetchResourceMasterByOrg,
  fetchResourcesByMember,
} from "../Api/projectResourceService";
import { fetchProjectsByOrg, fetchProjectDetails } from "../Api/ViewProjects";
import { fetchCustomersByOrgId, fetchCustomerByCode } from "../Api/InvoiceApi";
import * as XLSX from "xlsx";
import "./ResourceAllocationReport.css";

function ResourceAllocationReport() {
  const [resources, setResources] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [allAllocations, setAllAllocations] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [projectNames, setProjectNames] = useState({});
  const [projectClientMap, setProjectClientMap] = useState({}); // Map projects to client codes
  const [customerNameMap, setCustomerNameMap] = useState({}); // Map customer codes to customer names

  const [selectedResource, setSelectedResource] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState("");

  // Sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc", // 'asc' or 'desc'
  });

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const organisationId = userDetails?.organisationId || 2001;

  // --------------------------------------------------------
  // SORTING FUNCTIONALITY
  // --------------------------------------------------------
  const handleSort = (key) => {
    let direction = "asc";
    
    // If clicking the same column, toggle direction
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle different data types and null values
      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";

      // Convert to string for case-insensitive comparison
      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  };

  // Apply sorting whenever displayData or sortConfig changes
  useEffect(() => {
    if (displayData.length > 0 && sortConfig.key) {
      const sortedData = getSortedData(displayData);
      setDisplayData(sortedData);
    }
  }, [sortConfig]);

  // --------------------------------------------------------
  // FETCH CUSTOMERS
  // --------------------------------------------------------
  const loadCustomers = async () => {
    try {
      console.log("Fetching customers for organisation:", organisationId);
      const data = await fetchCustomersByOrgId(organisationId);
      console.log("Customers data:", data);
      setCustomers(data);

      // Build customer name mapping
      const nameMap = {};
      data.forEach((customer) => {
        nameMap[customer.customerCode] = customer.name;
      });
      setCustomerNameMap(nameMap);
    } catch (err) {
      setError("Failed to fetch customers: " + err.message);
      console.error("Error fetching customers:", err);
    }
  };

  // --------------------------------------------------------
  // FETCH CUSTOMER NAME BY CODE
  // --------------------------------------------------------
  const fetchCustomerName = async (customerCode) => {
    if (!customerCode) return "N/A";

    // Check if we already have the customer name cached
    if (customerNameMap[customerCode]) {
      return customerNameMap[customerCode];
    }

    try {
      console.log(`Fetching customer details for code: ${customerCode}`);
      const customerData = await fetchCustomerByCode(customerCode);
      const customerName = customerData.name || customerCode;

      // Update customer name cache
      setCustomerNameMap((prev) => ({
        ...prev,
        [customerCode]: customerName,
      }));

      return customerName;
    } catch (err) {
      console.error(`Error fetching customer ${customerCode}:`, err);
      return customerCode; // Return code as fallback
    }
  };

  // --------------------------------------------------------
  // FETCH PROJECT NAME BY ID
  // --------------------------------------------------------
  const fetchProjectName = async (projectId) => {
    if (!projectId) return { projectName: "N/A", projectClient: null };

    // Check if we already have the project name cached
    if (projectNames[projectId]) {
      return {
        projectName: projectNames[projectId],
        projectClient: projectClientMap[projectId] || null,
      };
    }

    try {
      console.log(`Fetching project details for: ${projectId}`);
      const projectData = await fetchProjectDetails(projectId);
      const projectName = projectData.prTitle || projectId;
      const projectClient = projectData.prClient || null;

      console.log("Project data:", projectData);

      // Update caches
      setProjectNames((prev) => ({
        ...prev,
        [projectId]: projectName,
      }));

      if (projectClient) {
        setProjectClientMap((prev) => ({
          ...prev,
          [projectId]: projectClient,
        }));
      }

      return { projectName, projectClient };
    } catch (err) {
      console.error(`Error fetching project ${projectId}:`, err);
      return { projectName: projectId, projectClient: null };
    }
  };

  // --------------------------------------------------------
  // UPDATED FILTERING LOGIC WITH CLIENT CODE COMPARISON
  // --------------------------------------------------------
  const applyFilters = async (
    allocationData,
    resourceList,
    resourceId,
    projectId,
    customerCode
  ) => {
    console.log(
      "Applying filters - resourceId:",
      resourceId,
      "projectId:",
      projectId,
      "customerCode:",
      customerCode
    );
    console.log("Input allocation data:", allocationData);

    setFilterLoading(true);
    let filtered = allocationData;

    try {
      // If customer selected - filter by client code
      if (customerCode) {
        console.log("Filtering by customer code:", customerCode);
        console.log("Project-Client mapping:", projectClientMap);

        // Filter projects by client code (direct comparison)
        const customerProjectIds = Object.keys(projectClientMap).filter(
          (projectId) => projectClientMap[projectId] === customerCode
        );

        console.log("Customer projects (by code):", customerProjectIds);

        filtered = filtered.filter((item) =>
          customerProjectIds.includes(item.projectBelongTo?.toString())
        );
        console.log("After customer filter:", filtered);
      }

      // If project selected
      if (projectId) {
        filtered = filtered.filter(
          (item) => item.projectBelongTo === projectId
        );
        console.log("After project filter:", filtered);
      }

      // Merge resource master details into allocation data
      const merged = await Promise.all(
        filtered.map(async (item) => {
          const master = resourceList.find(
            (r) => r.resourceId === item.resourceId
          );

          // Get project name and client info
          const projectInfo = await fetchProjectName(item.projectBelongTo);

          // Get customer name for display
          const customerName = await fetchCustomerName(
            projectInfo.projectClient
          );

          return {
            ...item,
            name: master?.name || item.name,
            emailId: master?.emailId || item.emailId,
            department: master?.department || item.department,
            role: master?.role || item.role,
            mav_UserID: master?.mav_UserID || item.mav_UserID,
            projectName: projectInfo.projectName,
            projectClient: projectInfo.projectClient, // This is the client code (for filtering)
            customerName: customerName, // This is the customer name (for display)
            // Add fields for sorting
            sortCustomerName: customerName,
            sortProjectName: projectInfo.projectName,
            sortResourceName: master?.name || item.name,
          };
        })
      );

      console.log("Final display data after merging:", merged);
      
      // Apply sorting if any sort is active
      if (sortConfig.key) {
        const sortedData = getSortedData(merged);
        setDisplayData(sortedData);
      } else {
        setDisplayData(merged);
      }
    } catch (err) {
      setError("Failed to apply filters: " + err.message);
      console.error("Error applying filters:", err);
    } finally {
      setFilterLoading(false);
    }
  };

  // --------------------------------------------------------
  // FETCH MISSING PROJECT NAMES AND CUSTOMER NAMES
  // --------------------------------------------------------
  const fetchMissingData = async (data) => {
    const uniqueProjectIds = [
      ...new Set(data.map((item) => item.projectBelongTo)),
    ];
    const missingProjectIds = uniqueProjectIds.filter(
      (projectId) => projectId && !projectNames[projectId]
    );

    // Get unique customer codes from projectClientMap
    const uniqueCustomerCodes = [
      ...new Set(
        data
          .map((item) => projectClientMap[item.projectBelongTo])
          .filter(Boolean)
      ),
    ];

    const missingCustomerCodes = uniqueCustomerCodes.filter(
      (customerCode) => customerCode && !customerNameMap[customerCode]
    );

    if (missingProjectIds.length === 0 && missingCustomerCodes.length === 0)
      return;

    console.log("Fetching missing project names for:", missingProjectIds);
    console.log("Fetching missing customer names for:", missingCustomerCodes);

    // Fetch missing project names
    const projectNamePromises = missingProjectIds.map(async (projectId) => {
      try {
        const projectData = await fetchProjectDetails(projectId);
        return {
          projectId,
          projectName: projectData.prTitle || projectId,
          projectClient: projectData.prClient || null,
        };
      } catch (err) {
        console.error(`Error fetching project ${projectId}:`, err);
        return {
          projectId,
          projectName: projectId,
          projectClient: null,
        };
      }
    });

    // Fetch missing customer names
    const customerNamePromises = missingCustomerCodes.map(
      async (customerCode) => {
        try {
          const customerData = await fetchCustomerByCode(customerCode);
          return {
            customerCode,
            customerName: customerData.name || customerCode,
          };
        } catch (err) {
          console.error(`Error fetching customer ${customerCode}:`, err);
          return {
            customerCode,
            customerName: customerCode,
          };
        }
      }
    );

    const [projectResults, customerResults] = await Promise.all([
      Promise.all(projectNamePromises),
      Promise.all(customerNamePromises),
    ]);

    const newProjectNames = {};
    const newProjectClientMap = {};
    const newCustomerNameMap = {};

    projectResults.forEach((result) => {
      newProjectNames[result.projectId] = result.projectName;
      if (result.projectClient) {
        newProjectClientMap[result.projectId] = result.projectClient;
      }
    });

    customerResults.forEach((result) => {
      newCustomerNameMap[result.customerCode] = result.customerName;
    });

    // Update caches
    setProjectNames((prev) => ({
      ...prev,
      ...newProjectNames,
    }));

    setProjectClientMap((prev) => ({
      ...prev,
      ...newProjectClientMap,
    }));

    setCustomerNameMap((prev) => ({
      ...prev,
      ...newCustomerNameMap,
    }));

    // Update display data with new project names and customer names
    const updatedDisplayData = data.map((item) => {
      const projectClient =
        newProjectClientMap[item.projectBelongTo] ||
        projectClientMap[item.projectBelongTo];

      const customerName =
        newCustomerNameMap[projectClient] ||
        customerNameMap[projectClient] ||
        projectClient;

      return {
        ...item,
        projectName:
          newProjectNames[item.projectBelongTo] ||
          projectNames[item.projectBelongTo] ||
          item.projectBelongTo,
        projectClient: projectClient,
        customerName: customerName,
        // Update sorting fields
        sortCustomerName: customerName,
        sortProjectName: newProjectNames[item.projectBelongTo] || projectNames[item.projectBelongTo] || item.projectBelongTo,
        sortResourceName: item.name,
      };
    });

    // Apply sorting if any sort is active
    if (sortConfig.key) {
      const sortedData = getSortedData(updatedDisplayData);
      setDisplayData(sortedData);
    } else {
      setDisplayData(updatedDisplayData);
    }
  };

  // --------------------------------------------------------
  // UPDATED PROJECT FILTER OPTIONS WITH CLIENT CODE COMPARISON
  // --------------------------------------------------------
  const getFilteredProjects = () => {
    if (!selectedCustomer) {
      return projects;
    }

    // Filter projects by client code (direct comparison)
    return projects.filter(
      (project) => projectClientMap[project.projectId] === selectedCustomer
    );
  };

  // --------------------------------------------------------
  // UPDATED EXCEL DOWNLOAD WITH CUSTOMER NAME
  // --------------------------------------------------------
  const downloadExcel = () => {
    if (displayData.length === 0) {
      alert("No data available to download.");
      return;
    }

    try {
      const excelData = displayData.map((item, index) => ({
        "S.No": index + 1,
        "Resource Name": item.name || "N/A",
        "Email ID": item.emailId || "N/A",
        Department: item.department || "N/A",
        Role: item.resourceRole || "N/A",
        "MAV User ID": item.mav_UserID || "N/A",
        "Allocation Percentage": `${item.resourceAllocaPercentage || "N/A"}%`,
        "Resource Status": item.resourceStatus || "N/A",
        "Project Name": item.projectName || "N/A",
        "Project ID": item.projectBelongTo || "N/A",
        "Client Code": item.projectClient || "N/A", // Client code
        "Client Name": item.customerName || "N/A", // Customer name
        "Resource ID": item.resourceId || "N/A",
        Billable: item.resourceBillable || "N/A",
        "Start Date": item.resourceStartDate
          ? new Date(item.resourceStartDate).toLocaleDateString()
          : "N/A",
        "End Date": item.resourceEndDate
          ? new Date(item.resourceEndDate).toLocaleDateString()
          : "N/A",
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      const colWidths = [
        { wch: 5 },
        { wch: 20 },
        { wch: 25 },
        { wch: 15 },
        { wch: 15 },
        { wch: 12 },
        { wch: 20 },
        { wch: 15 },
        { wch: 25 },
        { wch: 12 },
        { wch: 15 },
        { wch: 25 },
        { wch: 15 },
        { wch: 10 },
        { wch: 12 },
        { wch: 12 },
      ];
      ws["!cols"] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, "Resource Allocation");

      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      const fileName = `Resource_Allocation_Report_${timestamp}.xlsx`;

      XLSX.writeFile(wb, fileName);
      console.log("Excel file downloaded successfully");
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      setError("Failed to download Excel file: " + error.message);
    }
  };

  // --------------------------------------------------------
  // LOAD FUNCTIONS
  // --------------------------------------------------------
  const loadResources = async () => {
    try {
      setLoading(true);
      console.log(
        "Fetching resources master for organisation:",
        organisationId
      );
      const data = await fetchResourceMasterByOrg(organisationId);
      console.log("Resources master data:", data);
      setResources(data);
      loadAllResourceAllocations(data);
    } catch (err) {
      setError("Failed to fetch resources: " + err.message);
      console.error("Error fetching resources:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      console.log("Fetching projects for organisation:", organisationId);
      const data = await fetchProjectsByOrg(organisationId);
      console.log("Projects data:", data);
      setProjects(data);

      // Build project-client mapping with client codes
      const mapping = {};
      const projectDetailsPromises = data.map(async (project) => {
        try {
          const details = await fetchProjectDetails(project.projectId);
          if (details.prClient) {
            mapping[project.projectId] = details.prClient;
          }
        } catch (err) {
          console.error(
            `Error fetching project ${project.projectId} details:`,
            err
          );
        }
      });

      await Promise.all(projectDetailsPromises);
      setProjectClientMap(mapping);
    } catch (err) {
      setError("Failed to fetch projects: " + err.message);
      console.error("Error fetching projects:", err);
    }
  };

  const loadAllResourceAllocations = async (resourceList) => {
    try {
      setLoading(true);
      console.log(
        "Fetching allocations for all resources, count:",
        resourceList.length
      );

      const promises = resourceList.map(async (res) => {
        if (!res.mav_UserID) {
          console.log(`Skipping resource ${res.resourceId} - no mav_UserID`);
          return [];
        }
        try {
          console.log(
            `Fetching allocations for resource: ${res.resourceId}, mav_UserID: ${res.mav_UserID}`
          );
          return await fetchResourcesByMember(res.mav_UserID);
        } catch (err) {
          console.error(
            `Error fetching allocations for resource ${res.resourceId}:`,
            err
          );
          return [];
        }
      });

      const results = await Promise.all(promises);
      const flattened = results.flat();

      setAllAllocations(flattened);
      await applyFilters(
        flattened,
        resourceList,
        selectedResource,
        selectedProject,
        selectedCustomer
      );
    } catch (err) {
      setError("Failed to fetch allocation details: " + err.message);
      console.error("Error fetching all allocations:", err);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const loadSingleResourceAllocation = async (mavId) => {
    try {
      setFilterLoading(true);
      console.log(
        `Fetching allocations for single resource, mav_UserID: ${mavId}`
      );
      const data = await fetchResourcesByMember(mavId);
      console.log("Single resource allocation data:", data);
      await applyFilters(
        data,
        resources,
        selectedResource,
        selectedProject,
        selectedCustomer
      );
    } catch (err) {
      setError("Failed to fetch resource details: " + err.message);
      console.error("Error fetching single resource allocation:", err);
    } finally {
      setFilterLoading(false);
    }
  };

  // --------------------------------------------------------
  // FILTER HANDLERS
  // --------------------------------------------------------
  const handleResetFilters = async () => {
    console.log("Resetting all filters");
    setSelectedResource("");
    setSelectedProject("");
    setSelectedCustomer("");
    setSortConfig({ key: null, direction: "asc" }); // Reset sorting
    setFilterLoading(true);
    try {
      await applyFilters(allAllocations, resources, "", "", "");
    } finally {
      setFilterLoading(false);
    }
  };

  const handleResourceChange = async (e) => {
    const value = e.target.value;
    console.log("Resource changed to:", value);
    setSelectedResource(value);
    setFilterLoading(true);

    try {
      if (value) {
        const resObj = resources.find((r) => r.resourceId === value);
        console.log("Found resource object:", resObj);
        if (resObj?.mav_UserID) {
          await loadSingleResourceAllocation(resObj.mav_UserID);
        }
      } else {
        console.log("Resource filter cleared, showing all allocations");
        await applyFilters(
          allAllocations,
          resources,
          "",
          selectedProject,
          selectedCustomer
        );
      }
    } finally {
      setFilterLoading(false);
    }
  };

  const handleProjectChange = async (e) => {
    const value = e.target.value;
    console.log("Project changed to:", value);
    setSelectedProject(value);
    setFilterLoading(true);

    try {
      const sourceData = selectedResource ? displayData : allAllocations;

      console.log("Source data for project filter:", sourceData);
      await applyFilters(
        sourceData,
        resources,
        selectedResource,
        value,
        selectedCustomer
      );
    } finally {
      setFilterLoading(false);
    }
  };

  const handleCustomerChange = async (e) => {
    const value = e.target.value;
    console.log("Customer changed to:", value);
    setSelectedCustomer(value);
    setSelectedProject("");
    setFilterLoading(true);

    try {
      const sourceData = selectedResource ? displayData : allAllocations;

      console.log("Source data for customer filter:", sourceData);
      await applyFilters(sourceData, resources, selectedResource, "", value);
    } finally {
      setFilterLoading(false);
    }
  };

  // --------------------------------------------------------
  // EFFECT - INITIAL LOAD
  // --------------------------------------------------------
  useEffect(() => {
    console.log("Component mounted, initializing data load");
    const initializeData = async () => {
      setInitialLoading(true);
      try {
        await Promise.all([loadResources(), loadProjects(), loadCustomers()]);
      } catch (err) {
        setError("Failed to initialize data: " + err.message);
      } finally {
        setInitialLoading(false);
      }
    };

    initializeData();
  }, [organisationId]);

  // --------------------------------------------------------
  // RENDER SORT ICON
  // --------------------------------------------------------
  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return "↕️";
    }
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  // --------------------------------------------------------
  // UPDATED UI RENDERING WITH SORTABLE HEADERS
  // --------------------------------------------------------
  return (
    <div className="resource-allocation-report">
      <div className="report-header">
        <h2 className="report-title">Resource Allocation Report</h2>
        <p className="report-subtitle">
          View and filter resource allocations across projects and clients
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Initial Loading */}
      {initialLoading && (
        <div className="loading-container">
          <div className="loading-spinner large"></div>
          <p>Loading resource data...</p>
          <p className="loading-subtext">This may take a few moments</p>
        </div>
      )}

      {/* Filter Loading Overlay */}
      {filterLoading && (
        <div className="filter-loading-overlay">
          <div className="loading-spinner small"></div>
          <p>Applying filters...</p>
        </div>
      )}

      {!initialLoading && (
        <>
          {/* ---------------- FILTERS ---------------- */}
          <div className="filters-container">
            <div className="filter-group">
              <label className="filter-label">Resources</label>
              <select
                value={selectedResource}
                onChange={handleResourceChange}
                className="filter-select"
                disabled={filterLoading}
              >
                <option value="">All Resources</option>
                {resources.map((res) => (
                  <option key={res.resourceId} value={res.resourceId}>
                    {res.name} ({res.resourceId})
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Client</label>
              <select
                value={selectedCustomer}
                onChange={handleCustomerChange}
                className="filter-select"
                disabled={filterLoading}
              >
                <option value="">All Clients</option>
                {customers.map((customer) => (
                  <option
                    key={customer.customerCode}
                    value={customer.customerCode}
                  >
                    {customer.name} ({customer.customerCode})
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Projects</label>
              <select
                value={selectedProject}
                onChange={handleProjectChange}
                className="filter-select"
                disabled={!!selectedCustomer || filterLoading}
              >
                <option value="">All Projects</option>
                {getFilteredProjects().map((p) => (
                  <option key={p.projectId} value={p.projectId}>
                    {p.prTitle} ({p.projectId})
                  </option>
                ))}
              </select>
              {selectedCustomer && (
                <div className="filter-note">
                  Project list filtered by selected customer
                </div>
              )}
            </div>

            <button
              onClick={handleResetFilters}
              className="reset-button"
              disabled={filterLoading}
            >
              {filterLoading ? "Resetting..." : "Reset Filters"}
            </button>

            <button
              onClick={downloadExcel}
              className="download-excel-button"
              disabled={displayData.length === 0 || filterLoading}
            >
              {filterLoading ? "⏳" : "📊"} Download Excel
            </button>
          </div>

          {/* ---------------- TABLE OUTPUT ---------------- */}
          <div className="table-container">
            <div className="table-header">
              <h3>Resource Details</h3>
              <span className="results-count">
                {displayData.length} result{displayData.length !== 1 ? "s" : ""}{" "}
                found
              </span>
            </div>

            {filterLoading ? (
              <div className="table-loading">
                <div className="loading-spinner"></div>
                <p>Loading table data...</p>
              </div>
            ) : displayData.length > 0 ? (
              <div className="table-wrapper">
                <table className="resources-table">
                  <thead>
                    <tr>
                      <th 
                        className="sortable-header"
                        onClick={() => handleSort("sortCustomerName")}
                      >
                        Client {renderSortIcon("sortCustomerName")}
                      </th>
                      <th 
                        className="sortable-header"
                        onClick={() => handleSort("sortProjectName")}
                      >
                        Project Name {renderSortIcon("sortProjectName")}
                      </th>
                      <th 
                        className="sortable-header"
                        onClick={() => handleSort("sortResourceName")}
                      >
                        Name {renderSortIcon("sortResourceName")}
                      </th>
                      {/* <th>Email</th> */}
                      <th>Department</th>
                      <th>Role</th>
                      {/* <th>MAV User ID</th> */}
                      <th>Allocation Percentage</th>
                      <th>Resource Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {displayData.map((item, i) => (
                      <tr
                        key={i}
                        className={i % 2 === 0 ? "even-row" : "odd-row"}
                      >
                        <td>
                          <div className="client-info">
                            <div className="customer-name">
                              {item.customerName || "N/A"}
                            </div>
                            <div className="customer-code">
                              Code: {item.projectClient || "N/A"}
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="project-info">
                            <div className="project-name">
                              {item.projectName || "N/A"}
                            </div>
                            <div className="project-id">
                              ID: {item.projectBelongTo || "N/A"}
                            </div>
                          </div>
                        </td>
                        <td>{item.name || "N/A"}</td>
                        {/* <td>
                          <a
                            href={`mailto:${item.emailId}`}
                            className="email-link"
                          >
                            {item.emailId || "N/A"}
                          </a>
                        </td> */}

                        <td>{item.department || "N/A"}</td>

                        <td>
                          <span className="role-badge">
                            {item.resourceRole || "N/A"}
                          </span>
                        </td>

                        {/* <td>
                          <code className="mav-id">
                            {item.mav_UserID || "N/A"}
                          </code>
                        </td> */}

                        <td>{item.resourceAllocaPercentage || "N/A"}%</td>

                        <td>
                          <span
                            className={`status-badge status-${
                              item.resourceStatus?.toLowerCase() || "unknown"
                            }`}
                          >
                            {item.resourceStatus || "N/A"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              !filterLoading && (
                <div className="empty-state">
                  <div className="empty-state-icon">📊</div>
                  <h4>No data found</h4>
                  <p>No resources match your current filter criteria.</p>
                  <button
                    onClick={handleResetFilters}
                    className="reset-button secondary"
                  >
                    Reset Filters
                  </button>
                </div>
              )
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ResourceAllocationReport;

// import React, { useState, useEffect } from "react";
// import {
//   fetchResourceMasterByOrg,
//   fetchResourcesByMember,
// } from "../Api/projectResourceService";
// import { fetchProjectsByOrg, fetchProjectDetails } from "../Api/ViewProjects";
// import { fetchCustomersByOrgId, fetchCustomerByCode } from "../Api/InvoiceApi";
// import * as XLSX from "xlsx";
// import "./ResourceAllocationReport.css";

// function ResourceAllocationReport() {
//   const [resources, setResources] = useState([]);
//   const [projects, setProjects] = useState([]);
//   const [customers, setCustomers] = useState([]);
//   const [allAllocations, setAllAllocations] = useState([]);
//   const [displayData, setDisplayData] = useState([]);
//   const [projectNames, setProjectNames] = useState({});
//   const [projectClientMap, setProjectClientMap] = useState({}); // Map projects to client codes
//   const [customerNameMap, setCustomerNameMap] = useState({}); // Map customer codes to customer names

//   const [selectedResource, setSelectedResource] = useState("");
//   const [selectedProject, setSelectedProject] = useState("");
//   const [selectedCustomer, setSelectedCustomer] = useState("");

//   const [loading, setLoading] = useState(false);
//   const [initialLoading, setInitialLoading] = useState(true);
//   const [filterLoading, setFilterLoading] = useState(false);
//   const [error, setError] = useState("");

//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const organisationId = userDetails?.organisationId || 2001;

//   // --------------------------------------------------------
//   // FETCH CUSTOMERS
//   // --------------------------------------------------------
//   const loadCustomers = async () => {
//     try {
//       console.log("Fetching customers for organisation:", organisationId);
//       const data = await fetchCustomersByOrgId(organisationId);
//       console.log("Customers data:", data);
//       setCustomers(data);

//       // Build customer name mapping
//       const nameMap = {};
//       data.forEach((customer) => {
//         nameMap[customer.customerCode] = customer.name;
//       });
//       setCustomerNameMap(nameMap);
//     } catch (err) {
//       setError("Failed to fetch customers: " + err.message);
//       console.error("Error fetching customers:", err);
//     }
//   };

//   // --------------------------------------------------------
//   // FETCH CUSTOMER NAME BY CODE
//   // --------------------------------------------------------
//   const fetchCustomerName = async (customerCode) => {
//     if (!customerCode) return "N/A";

//     // Check if we already have the customer name cached
//     if (customerNameMap[customerCode]) {
//       return customerNameMap[customerCode];
//     }

//     try {
//       console.log(`Fetching customer details for code: ${customerCode}`);
//       const customerData = await fetchCustomerByCode(customerCode);
//       const customerName = customerData.name || customerCode;

//       // Update customer name cache
//       setCustomerNameMap((prev) => ({
//         ...prev,
//         [customerCode]: customerName,
//       }));

//       return customerName;
//     } catch (err) {
//       console.error(`Error fetching customer ${customerCode}:`, err);
//       return customerCode; // Return code as fallback
//     }
//   };

//   // --------------------------------------------------------
//   // FETCH PROJECT NAME BY ID
//   // --------------------------------------------------------
//   const fetchProjectName = async (projectId) => {
//     if (!projectId) return { projectName: "N/A", projectClient: null };

//     // Check if we already have the project name cached
//     if (projectNames[projectId]) {
//       return {
//         projectName: projectNames[projectId],
//         projectClient: projectClientMap[projectId] || null,
//       };
//     }

//     try {
//       console.log(`Fetching project details for: ${projectId}`);
//       const projectData = await fetchProjectDetails(projectId);
//       const projectName = projectData.prTitle || projectId;
//       const projectClient = projectData.prClient || null;

//       console.log("Project data:", projectData);

//       // Update caches
//       setProjectNames((prev) => ({
//         ...prev,
//         [projectId]: projectName,
//       }));

//       if (projectClient) {
//         setProjectClientMap((prev) => ({
//           ...prev,
//           [projectId]: projectClient,
//         }));
//       }

//       return { projectName, projectClient };
//     } catch (err) {
//       console.error(`Error fetching project ${projectId}:`, err);
//       return { projectName: projectId, projectClient: null };
//     }
//   };

//   // --------------------------------------------------------
//   // UPDATED FILTERING LOGIC WITH CLIENT CODE COMPARISON
//   // --------------------------------------------------------
//   const applyFilters = async (
//     allocationData,
//     resourceList,
//     resourceId,
//     projectId,
//     customerCode
//   ) => {
//     console.log(
//       "Applying filters - resourceId:",
//       resourceId,
//       "projectId:",
//       projectId,
//       "customerCode:",
//       customerCode
//     );
//     console.log("Input allocation data:", allocationData);

//     setFilterLoading(true);
//     let filtered = allocationData;

//     try {
//       // If customer selected - filter by client code
//       if (customerCode) {
//         console.log("Filtering by customer code:", customerCode);
//         console.log("Project-Client mapping:", projectClientMap);

//         // Filter projects by client code (direct comparison)
//         const customerProjectIds = Object.keys(projectClientMap).filter(
//           (projectId) => projectClientMap[projectId] === customerCode
//         );

//         console.log("Customer projects (by code):", customerProjectIds);

//         filtered = filtered.filter((item) =>
//           customerProjectIds.includes(item.projectBelongTo?.toString())
//         );
//         console.log("After customer filter:", filtered);
//       }

//       // If project selected
//       if (projectId) {
//         filtered = filtered.filter(
//           (item) => item.projectBelongTo === projectId
//         );
//         console.log("After project filter:", filtered);
//       }

//       // Merge resource master details into allocation data
//       const merged = await Promise.all(
//         filtered.map(async (item) => {
//           const master = resourceList.find(
//             (r) => r.resourceId === item.resourceId
//           );

//           // Get project name and client info
//           const projectInfo = await fetchProjectName(item.projectBelongTo);

//           // Get customer name for display
//           const customerName = await fetchCustomerName(
//             projectInfo.projectClient
//           );

//           return {
//             ...item,
//             name: master?.name || item.name,
//             emailId: master?.emailId || item.emailId,
//             department: master?.department || item.department,
//             role: master?.role || item.role,
//             mav_UserID: master?.mav_UserID || item.mav_UserID,
//             projectName: projectInfo.projectName,
//             projectClient: projectInfo.projectClient, // This is the client code (for filtering)
//             customerName: customerName, // This is the customer name (for display)
//           };
//         })
//       );

//       console.log("Final display data after merging:", merged);
//       setDisplayData(merged);
//     } catch (err) {
//       setError("Failed to apply filters: " + err.message);
//       console.error("Error applying filters:", err);
//     } finally {
//       setFilterLoading(false);
//     }
//   };

//   // --------------------------------------------------------
//   // FETCH MISSING PROJECT NAMES AND CUSTOMER NAMES
//   // --------------------------------------------------------
//   const fetchMissingData = async (data) => {
//     const uniqueProjectIds = [
//       ...new Set(data.map((item) => item.projectBelongTo)),
//     ];
//     const missingProjectIds = uniqueProjectIds.filter(
//       (projectId) => projectId && !projectNames[projectId]
//     );

//     // Get unique customer codes from projectClientMap
//     const uniqueCustomerCodes = [
//       ...new Set(
//         data
//           .map((item) => projectClientMap[item.projectBelongTo])
//           .filter(Boolean)
//       ),
//     ];

//     const missingCustomerCodes = uniqueCustomerCodes.filter(
//       (customerCode) => customerCode && !customerNameMap[customerCode]
//     );

//     if (missingProjectIds.length === 0 && missingCustomerCodes.length === 0)
//       return;

//     console.log("Fetching missing project names for:", missingProjectIds);
//     console.log("Fetching missing customer names for:", missingCustomerCodes);

//     // Fetch missing project names
//     const projectNamePromises = missingProjectIds.map(async (projectId) => {
//       try {
//         const projectData = await fetchProjectDetails(projectId);
//         return {
//           projectId,
//           projectName: projectData.prTitle || projectId,
//           projectClient: projectData.prClient || null,
//         };
//       } catch (err) {
//         console.error(`Error fetching project ${projectId}:`, err);
//         return {
//           projectId,
//           projectName: projectId,
//           projectClient: null,
//         };
//       }
//     });

//     // Fetch missing customer names
//     const customerNamePromises = missingCustomerCodes.map(
//       async (customerCode) => {
//         try {
//           const customerData = await fetchCustomerByCode(customerCode);
//           return {
//             customerCode,
//             customerName: customerData.name || customerCode,
//           };
//         } catch (err) {
//           console.error(`Error fetching customer ${customerCode}:`, err);
//           return {
//             customerCode,
//             customerName: customerCode,
//           };
//         }
//       }
//     );

//     const [projectResults, customerResults] = await Promise.all([
//       Promise.all(projectNamePromises),
//       Promise.all(customerNamePromises),
//     ]);

//     const newProjectNames = {};
//     const newProjectClientMap = {};
//     const newCustomerNameMap = {};

//     projectResults.forEach((result) => {
//       newProjectNames[result.projectId] = result.projectName;
//       if (result.projectClient) {
//         newProjectClientMap[result.projectId] = result.projectClient;
//       }
//     });

//     customerResults.forEach((result) => {
//       newCustomerNameMap[result.customerCode] = result.customerName;
//     });

//     // Update caches
//     setProjectNames((prev) => ({
//       ...prev,
//       ...newProjectNames,
//     }));

//     setProjectClientMap((prev) => ({
//       ...prev,
//       ...newProjectClientMap,
//     }));

//     setCustomerNameMap((prev) => ({
//       ...prev,
//       ...newCustomerNameMap,
//     }));

//     // Update display data with new project names and customer names
//     const updatedDisplayData = data.map((item) => {
//       const projectClient =
//         newProjectClientMap[item.projectBelongTo] ||
//         projectClientMap[item.projectBelongTo];

//       const customerName =
//         newCustomerNameMap[projectClient] ||
//         customerNameMap[projectClient] ||
//         projectClient;

//       return {
//         ...item,
//         projectName:
//           newProjectNames[item.projectBelongTo] ||
//           projectNames[item.projectBelongTo] ||
//           item.projectBelongTo,
//         projectClient: projectClient,
//         customerName: customerName,
//       };
//     });

//     setDisplayData(updatedDisplayData);
//   };

//   // --------------------------------------------------------
//   // UPDATED PROJECT FILTER OPTIONS WITH CLIENT CODE COMPARISON
//   // --------------------------------------------------------
//   const getFilteredProjects = () => {
//     if (!selectedCustomer) {
//       return projects;
//     }

//     // Filter projects by client code (direct comparison)
//     return projects.filter(
//       (project) => projectClientMap[project.projectId] === selectedCustomer
//     );
//   };

//   // --------------------------------------------------------
//   // UPDATED EXCEL DOWNLOAD WITH CUSTOMER NAME
//   // --------------------------------------------------------
//   const downloadExcel = () => {
//     if (displayData.length === 0) {
//       alert("No data available to download.");
//       return;
//     }

//     try {
//       const excelData = displayData.map((item, index) => ({
//         "S.No": index + 1,
//         "Resource Name": item.name || "N/A",
//         "Email ID": item.emailId || "N/A",
//         Department: item.department || "N/A",
//         Role: item.resourceRole || "N/A",
//         "MAV User ID": item.mav_UserID || "N/A",
//         "Allocation Percentage": `${item.resourceAllocaPercentage || "N/A"}%`,
//         "Resource Status": item.resourceStatus || "N/A",
//         "Project Name": item.projectName || "N/A",
//         "Project ID": item.projectBelongTo || "N/A",
//         "Client Code": item.projectClient || "N/A", // Client code
//         "Client Name": item.customerName || "N/A", // Customer name
//         "Resource ID": item.resourceId || "N/A",
//         Billable: item.resourceBillable || "N/A",
//         "Start Date": item.resourceStartDate
//           ? new Date(item.resourceStartDate).toLocaleDateString()
//           : "N/A",
//         "End Date": item.resourceEndDate
//           ? new Date(item.resourceEndDate).toLocaleDateString()
//           : "N/A",
//       }));

//       const wb = XLSX.utils.book_new();
//       const ws = XLSX.utils.json_to_sheet(excelData);

//       const colWidths = [
//         { wch: 5 },
//         { wch: 20 },
//         { wch: 25 },
//         { wch: 15 },
//         { wch: 15 },
//         { wch: 12 },
//         { wch: 20 },
//         { wch: 15 },
//         { wch: 25 },
//         { wch: 12 },
//         { wch: 15 },
//         { wch: 25 },
//         { wch: 15 },
//         { wch: 10 },
//         { wch: 12 },
//         { wch: 12 },
//       ];
//       ws["!cols"] = colWidths;

//       XLSX.utils.book_append_sheet(wb, ws, "Resource Allocation");

//       const timestamp = new Date()
//         .toISOString()
//         .slice(0, 19)
//         .replace(/:/g, "-");
//       const fileName = `Resource_Allocation_Report_${timestamp}.xlsx`;

//       XLSX.writeFile(wb, fileName);
//       console.log("Excel file downloaded successfully");
//     } catch (error) {
//       console.error("Error downloading Excel file:", error);
//       setError("Failed to download Excel file: " + error.message);
//     }
//   };

//   // --------------------------------------------------------
//   // LOAD FUNCTIONS
//   // --------------------------------------------------------
//   const loadResources = async () => {
//     try {
//       setLoading(true);
//       console.log(
//         "Fetching resources master for organisation:",
//         organisationId
//       );
//       const data = await fetchResourceMasterByOrg(organisationId);
//       console.log("Resources master data:", data);
//       setResources(data);
//       loadAllResourceAllocations(data);
//     } catch (err) {
//       setError("Failed to fetch resources: " + err.message);
//       console.error("Error fetching resources:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const loadProjects = async () => {
//     try {
//       console.log("Fetching projects for organisation:", organisationId);
//       const data = await fetchProjectsByOrg(organisationId);
//       console.log("Projects data:", data);
//       setProjects(data);

//       // Build project-client mapping with client codes
//       const mapping = {};
//       const projectDetailsPromises = data.map(async (project) => {
//         try {
//           const details = await fetchProjectDetails(project.projectId);
//           if (details.prClient) {
//             mapping[project.projectId] = details.prClient;
//           }
//         } catch (err) {
//           console.error(
//             `Error fetching project ${project.projectId} details:`,
//             err
//           );
//         }
//       });

//       await Promise.all(projectDetailsPromises);
//       setProjectClientMap(mapping);
//     } catch (err) {
//       setError("Failed to fetch projects: " + err.message);
//       console.error("Error fetching projects:", err);
//     }
//   };

//   const loadAllResourceAllocations = async (resourceList) => {
//     try {
//       setLoading(true);
//       console.log(
//         "Fetching allocations for all resources, count:",
//         resourceList.length
//       );

//       const promises = resourceList.map(async (res) => {
//         if (!res.mav_UserID) {
//           console.log(`Skipping resource ${res.resourceId} - no mav_UserID`);
//           return [];
//         }
//         try {
//           console.log(
//             `Fetching allocations for resource: ${res.resourceId}, mav_UserID: ${res.mav_UserID}`
//           );
//           return await fetchResourcesByMember(res.mav_UserID);
//         } catch (err) {
//           console.error(
//             `Error fetching allocations for resource ${res.resourceId}:`,
//             err
//           );
//           return [];
//         }
//       });

//       const results = await Promise.all(promises);
//       const flattened = results.flat();

//       setAllAllocations(flattened);
//       await applyFilters(
//         flattened,
//         resourceList,
//         selectedResource,
//         selectedProject,
//         selectedCustomer
//       );
//     } catch (err) {
//       setError("Failed to fetch allocation details: " + err.message);
//       console.error("Error fetching all allocations:", err);
//     } finally {
//       setLoading(false);
//       setInitialLoading(false);
//     }
//   };

//   const loadSingleResourceAllocation = async (mavId) => {
//     try {
//       setFilterLoading(true);
//       console.log(
//         `Fetching allocations for single resource, mav_UserID: ${mavId}`
//       );
//       const data = await fetchResourcesByMember(mavId);
//       console.log("Single resource allocation data:", data);
//       await applyFilters(
//         data,
//         resources,
//         selectedResource,
//         selectedProject,
//         selectedCustomer
//       );
//     } catch (err) {
//       setError("Failed to fetch resource details: " + err.message);
//       console.error("Error fetching single resource allocation:", err);
//     } finally {
//       setFilterLoading(false);
//     }
//   };

//   // --------------------------------------------------------
//   // FILTER HANDLERS
//   // --------------------------------------------------------
//   const handleResetFilters = async () => {
//     console.log("Resetting all filters");
//     setSelectedResource("");
//     setSelectedProject("");
//     setSelectedCustomer("");
//     setFilterLoading(true);
//     try {
//       await applyFilters(allAllocations, resources, "", "", "");
//     } finally {
//       setFilterLoading(false);
//     }
//   };

//   const handleResourceChange = async (e) => {
//     const value = e.target.value;
//     console.log("Resource changed to:", value);
//     setSelectedResource(value);
//     setFilterLoading(true);

//     try {
//       if (value) {
//         const resObj = resources.find((r) => r.resourceId === value);
//         console.log("Found resource object:", resObj);
//         if (resObj?.mav_UserID) {
//           await loadSingleResourceAllocation(resObj.mav_UserID);
//         }
//       } else {
//         console.log("Resource filter cleared, showing all allocations");
//         await applyFilters(
//           allAllocations,
//           resources,
//           "",
//           selectedProject,
//           selectedCustomer
//         );
//       }
//     } finally {
//       setFilterLoading(false);
//     }
//   };

//   const handleProjectChange = async (e) => {
//     const value = e.target.value;
//     console.log("Project changed to:", value);
//     setSelectedProject(value);
//     setFilterLoading(true);

//     try {
//       const sourceData = selectedResource ? displayData : allAllocations;

//       console.log("Source data for project filter:", sourceData);
//       await applyFilters(
//         sourceData,
//         resources,
//         selectedResource,
//         value,
//         selectedCustomer
//       );
//     } finally {
//       setFilterLoading(false);
//     }
//   };

//   const handleCustomerChange = async (e) => {
//     const value = e.target.value;
//     console.log("Customer changed to:", value);
//     setSelectedCustomer(value);
//     setSelectedProject("");
//     setFilterLoading(true);

//     try {
//       const sourceData = selectedResource ? displayData : allAllocations;

//       console.log("Source data for customer filter:", sourceData);
//       await applyFilters(sourceData, resources, selectedResource, "", value);
//     } finally {
//       setFilterLoading(false);
//     }
//   };

//   // --------------------------------------------------------
//   // EFFECT - INITIAL LOAD
//   // --------------------------------------------------------
//   useEffect(() => {
//     console.log("Component mounted, initializing data load");
//     const initializeData = async () => {
//       setInitialLoading(true);
//       try {
//         await Promise.all([loadResources(), loadProjects(), loadCustomers()]);
//       } catch (err) {
//         setError("Failed to initialize data: " + err.message);
//       } finally {
//         setInitialLoading(false);
//       }
//     };

//     initializeData();
//   }, [organisationId]);

//   // --------------------------------------------------------
//   // UPDATED UI RENDERING WITH LOADERS
//   // --------------------------------------------------------
//   return (
//     <div className="resource-allocation-report">
//       <div className="report-header">
//         <h2 className="report-title">Resource Allocation Report</h2>
//         <p className="report-subtitle">
//           View and filter resource allocations across projects and clients
//         </p>
//       </div>

//       {error && <div className="error-message">{error}</div>}

//       {/* Initial Loading */}
//       {initialLoading && (
//         <div className="loading-container">
//           <div className="loading-spinner large"></div>
//           <p>Loading resource data...</p>
//           <p className="loading-subtext">This may take a few moments</p>
//         </div>
//       )}

//       {/* Filter Loading Overlay */}
//       {filterLoading && (
//         <div className="filter-loading-overlay">
//           <div className="loading-spinner small"></div>
//           <p>Applying filters...</p>
//         </div>
//       )}

//       {!initialLoading && (
//         <>
//           {/* ---------------- FILTERS ---------------- */}
//           <div className="filters-container">
//             <div className="filter-group">
//               <label className="filter-label">Resources</label>
//               <select
//                 value={selectedResource}
//                 onChange={handleResourceChange}
//                 className="filter-select"
//                 disabled={filterLoading}
//               >
//                 <option value="">All Resources</option>
//                 {resources.map((res) => (
//                   <option key={res.resourceId} value={res.resourceId}>
//                     {res.name} ({res.resourceId})
//                   </option>
//                 ))}
//               </select>
//             </div>

//             <div className="filter-group">
//               <label className="filter-label">Client</label>
//               <select
//                 value={selectedCustomer}
//                 onChange={handleCustomerChange}
//                 className="filter-select"
//                 disabled={filterLoading}
//               >
//                 <option value="">All Clients</option>
//                 {customers.map((customer) => (
//                   <option
//                     key={customer.customerCode}
//                     value={customer.customerCode}
//                   >
//                     {customer.name} ({customer.customerCode})
//                   </option>
//                 ))}
//               </select>
//             </div>

//             <div className="filter-group">
//               <label className="filter-label">Projects</label>
//               <select
//                 value={selectedProject}
//                 onChange={handleProjectChange}
//                 className="filter-select"
//                 disabled={!!selectedCustomer || filterLoading}
//               >
//                 <option value="">All Projects</option>
//                 {getFilteredProjects().map((p) => (
//                   <option key={p.projectId} value={p.projectId}>
//                     {p.prTitle} ({p.projectId})
//                   </option>
//                 ))}
//               </select>
//               {selectedCustomer && (
//                 <div className="filter-note">
//                   Project list filtered by selected customer
//                 </div>
//               )}
//             </div>

//             <button
//               onClick={handleResetFilters}
//               className="reset-button"
//               disabled={filterLoading}
//             >
//               {filterLoading ? "Resetting..." : "Reset Filters"}
//             </button>

//             <button
//               onClick={downloadExcel}
//               className="download-excel-button"
//               disabled={displayData.length === 0 || filterLoading}
//             >
//               {filterLoading ? "⏳" : "📊"} Download Excel
//             </button>
//           </div>

//           {/* ---------------- TABLE OUTPUT ---------------- */}
//           <div className="table-container">
//             <div className="table-header">
//               <h3>Resource Details</h3>
//               <span className="results-count">
//                 {displayData.length} result{displayData.length !== 1 ? "s" : ""}{" "}
//                 found
//               </span>
//             </div>

//             {filterLoading ? (
//               <div className="table-loading">
//                 <div className="loading-spinner"></div>
//                 <p>Loading table data...</p>
//               </div>
//             ) : displayData.length > 0 ? (
//               <div className="table-wrapper">
//                 <table className="resources-table">
//                   <thead>
//                     <tr>
//                       <th>Client</th>
//                       <th>Project Name</th>
//                       <th>Name</th>
//                       {/* <th>Email</th> */}
//                       <th>Department</th>
//                       <th>Role</th>
//                       {/* <th>MAV User ID</th> */}
//                       <th>Allocation Percentage</th>
//                       <th>Resource Status</th>
//                     </tr>
//                   </thead>

//                   <tbody>
//                     {displayData.map((item, i) => (
//                       <tr
//                         key={i}
//                         className={i % 2 === 0 ? "even-row" : "odd-row"}
//                       >
//                         <td>
//                           <div className="client-info">
//                             <div className="customer-name">
//                               {item.customerName || "N/A"}
//                             </div>
//                             <div className="customer-code">
//                               Code: {item.projectClient || "N/A"}
//                             </div>
//                           </div>
//                         </td>

//                         <td>
//                           <div className="project-info">
//                             <div className="project-name">
//                               {item.projectName || "N/A"}
//                             </div>
//                             <div className="project-id">
//                               ID: {item.projectBelongTo || "N/A"}
//                             </div>
//                           </div>
//                         </td>
//                         <td>{item.name || "N/A"}</td>
//                         {/* <td>
//                           <a
//                             href={`mailto:${item.emailId}`}
//                             className="email-link"
//                           >
//                             {item.emailId || "N/A"}
//                           </a>
//                         </td> */}

//                         <td>{item.department || "N/A"}</td>

//                         <td>
//                           <span className="role-badge">
//                             {item.resourceRole || "N/A"}
//                           </span>
//                         </td>

//                         {/* <td>
//                           <code className="mav-id">
//                             {item.mav_UserID || "N/A"}
//                           </code>
//                         </td> */}

//                         <td>{item.resourceAllocaPercentage || "N/A"}%</td>

//                         <td>
//                           <span
//                             className={`status-badge status-${
//                               item.resourceStatus?.toLowerCase() || "unknown"
//                             }`}
//                           >
//                             {item.resourceStatus || "N/A"}
//                           </span>
//                         </td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             ) : (
//               !filterLoading && (
//                 <div className="empty-state">
//                   <div className="empty-state-icon">📊</div>
//                   <h4>No data found</h4>
//                   <p>No resources match your current filter criteria.</p>
//                   <button
//                     onClick={handleResetFilters}
//                     className="reset-button secondary"
//                   >
//                     Reset Filters
//                   </button>
//                 </div>
//               )
//             )}
//           </div>
//         </>
//       )}
//     </div>
//   );
// }

// export default ResourceAllocationReport;
